// Household (family membership) panel.
//
// Shows the caller's household and its members. The OWNER can name the
// household, invite family members to shared seats, and remove them. Family
// members see a read-only view. If the caller has no household yet, the owner
// can create one (a family membership shares the owner's paid seat).
//
// Seat model (mirrors api/src/data/households.js): one paid membership = one
// household; family logins consume household seats (maxSeats), not club seats.

import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  fetchHousehold,
  createHousehold,
  inviteHouseholdMember,
  removeHouseholdMember,
} from '../api';

export default function Household({ memberId }) {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  // getActiveAccount() returns a fresh object each render; depending on the raw
  // object in useCallback recreates load() every render, refiring the effect in
  // an infinite setLoading loop (panel stuck on "Loading…"). Key off a stable
  // string instead, mirroring the other member panels.
  const accountKey = account?.homeAccountId || account?.username || null;

  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHousehold(instance, account);
      if (res.ok) {
        setHousehold(res.data && res.data.household);
      } else {
        setError((res.data && (res.data.message || res.data.error)) || 'Could not load your household.');
      }
    } catch {
      setError('Network error loading your household.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accountKey]);

  useEffect(() => { load(); }, [load]);

  const isOwner = household && memberId && household.ownerMemberId === memberId;

  async function handleCreate() {
    setBusy(true);
    setError(null);
    try {
      const res = await createHousehold(instance, account, {});
      if (res.ok) {
        setHousehold(res.data && res.data.household);
        setNotice('Family membership created. Invite your family below.');
      } else {
        setError((res.data && (res.data.message || res.data.error)) || 'Could not create your household.');
      }
    } catch {
      setError('Network error creating your household.');
    } finally {
      setBusy(false);
    }
  }

  async function handleInvite(e) {
    e.preventDefault();
    setError(null);
    setInviteLink(null);
    setNotice(null);
    const email = inviteEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const res = await inviteHouseholdMember(instance, account, { email });
      if (res.ok) {
        setInviteEmail('');
        setInviteLink(res.data && res.data.acceptUrl);
        setNotice(`Invite sent to ${email}.`);
        await load();
      } else {
        setError((res.data && (res.data.message || res.data.error)) || 'Could not send the invite.');
      }
    } catch {
      setError('Network error sending the invite.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(target) {
    if (!window.confirm(`Remove ${target.display_name || target.email} from your family membership?`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await removeHouseholdMember(instance, account, target.id);
      if (res.ok) {
        setHousehold(res.data && res.data.household);
        setNotice('Family member removed.');
      } else {
        setError((res.data && (res.data.message || res.data.error)) || 'Could not remove that member.');
      }
    } catch {
      setError('Network error removing the member.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="card">Loading your family membership…</div>;

  // No household yet — offer to start one.
  if (!household) {
    return (
      <div className="card">
        <h2>Family Membership</h2>
        <p className="muted">
          Share your membership with your family. Start a family membership and invite
          your household to their own logins under your seat.
        </p>
        {error && <p className="app-error" role="alert">{error}</p>}
        {notice && <p className="hh-notice">{notice}</p>}
        <button className="primary" onClick={handleCreate} disabled={busy}>
          {busy ? 'Creating…' : 'Start a family membership'}
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Family Membership</h2>
      <p className="muted">
        {household.name ? household.name : 'Your household'} ·{' '}
        {household.seatsUsed} of {household.maxSeats} seats used
        {household.seatsAvailable > 0
          ? ` · ${household.seatsAvailable} available`
          : ' · full'}
      </p>

      {error && <p className="app-error" role="alert">{error}</p>}
      {notice && <p className="hh-notice">{notice}</p>}

      <ul className="hh-list">
        {household.members.map((m) => (
          <li className="hh-row" key={m.id}>
            <div className="hh-row-main">
              <strong>{m.display_name || '—'}</strong>
              {m.is_owner && <span className="hh-badge">Owner</span>}
              {m.role === 'admin' && <span className="hh-badge hh-badge-admin">Admin</span>}
              <div className="muted">{m.email}</div>
            </div>
            {isOwner && !m.is_owner && (
              <button
                className="chip chip-danger"
                disabled={busy}
                onClick={() => handleRemove(m)}
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>

      {isOwner ? (
        <form className="hh-invite" onSubmit={handleInvite}>
          <h3>Invite a family member</h3>
          {household.seatsAvailable <= 0 ? (
            <p className="muted">Your household is full. Remove a member to free a seat.</p>
          ) : (
            <>
              <div className="hh-invite-row">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="family@example.com"
                  maxLength={256}
                  autoComplete="off"
                />
                <button type="submit" className="primary" disabled={busy}>
                  {busy ? 'Sending…' : 'Send invite'}
                </button>
              </div>
              {inviteLink && (
                <p className="hh-invite-link">
                  Invite link (also emailed):{' '}
                  <a href={inviteLink} target="_blank" rel="noreferrer">{inviteLink}</a>
                </p>
              )}
            </>
          )}
        </form>
      ) : (
        <p className="muted">You're part of this family membership. Only the owner can manage seats.</p>
      )}
    </div>
  );
}
